import { ENDPOINT } from "../../services/api-endpoint";
import { getList, getListById, getLists } from "../../services/services";
import {
  extractPartnerOrderLineRecords,
} from "../mes-orders/partner-order-detail-map";
import {
  extractPartnerVendorOrdersPayload,
  normalizeManualOrder,
  normalizePlatformVendorOrder,
  sortOrdersByCreatedDesc,
  type OrderSource,
} from "../mes-orders/mes-order-utils";

export type WorkflowOrderOption = {
  orderKey: string;
  id: number;
  ordercode: string;
  source: OrderSource;
  listRow?: Record<string, unknown>;
};

/** Manual = MES catalog part id. Platform = order API line (value prefix `line:`). */
export type WorkflowPartOption = {
  value: string;
  partname: string;
  partid?: number | null;
  orderPartName?: string | null;
  orderLineId?: number | null;
};

export function buildWorkflowOrderKey(source: OrderSource, id: number): string {
  return `${source}:${id}`;
}

export function parseWorkflowOrderKey(
  key: string,
): { source: OrderSource; id: number } | null {
  const trimmed = key.trim();
  const sep = trimmed.indexOf(":");
  if (sep <= 0) return null;
  const source = trimmed.slice(0, sep) as OrderSource;
  if (source !== "manual" && source !== "platform") return null;
  const id = Number(trimmed.slice(sep + 1));
  if (!Number.isFinite(id) || id <= 0) return null;
  return { source, id };
}

export async function fetchWorkflowOrderOptions(): Promise<WorkflowOrderOption[]> {
  const [list, platRaw] = await Promise.all([
    getLists(ENDPOINT.ORDER_LIST, {
      pagesize: 500,
      pagenumber: 0,
      serachtext: "",
    }),
    getList(ENDPOINT.PARTNER_VENDOR_ACTIVE_POS, {
      page: 1,
      search: "",
    }).catch(() => null),
  ]);

  const rows = (list?.data as unknown[]) || [];
  const manualUi = rows.map((r: unknown) =>
    normalizeManualOrder(r, (id) => (id != null ? `Client #${id}` : "—")),
  );

  let platformUi: ReturnType<typeof normalizePlatformVendorOrder>[] = [];
  if (platRaw != null) {
    const extracted = extractPartnerVendorOrdersPayload(platRaw);
    platformUi = extracted.rows.map((r) => normalizePlatformVendorOrder(r));
  }

  return sortOrdersByCreatedDesc([...platformUi, ...manualUi])
    .filter((o) => Number.isFinite(o.id) && o.id > 0)
    .map((o) => ({
      orderKey: buildWorkflowOrderKey(o.source, o.id),
      id: o.id,
      ordercode: o.ordercode,
      source: o.source,
      listRow:
        o.source === "platform" && o._raw && typeof o._raw === "object"
          ? (o._raw as Record<string, unknown>)
          : undefined,
    }));
}

function unwrapPartsList(raw: unknown): { id?: number; partname?: string | null }[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: { id?: number; partname?: string | null }[] }).data;
  }
  return [];
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v != null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function extractPlatformLinePartName(line: Record<string, unknown>): string {
  const modelAttrs = asRecord(line.model_attributes) ?? asRecord(line.modelAttributes);
  const fileName = str(modelAttrs?.file_name ?? modelAttrs?.fileName);
  if (fileName) return fileName;

  const cleanedModel = asRecord(line.cleaned_model) ?? asRecord(line.cleanedModel);
  return str(cleanedModel?.file_name ?? cleanedModel?.original_filename);
}

function extractLineItemId(line: Record<string, unknown>): number | null {
  const modelAttrs = asRecord(line.model_attributes) ?? asRecord(line.modelAttributes);
  const id = Number(
    line.vendor_bid_item_id ??
      line.vendorBidItemId ??
      line.vendor_order_item_id ??
      line.vendorOrderItemId ??
      line.id ??
      line.order_line_id ??
      line.orderLineId ??
      modelAttrs?.id ??
      NaN,
  );
  if (Number.isFinite(id) && id > 0) return id;
  return null;
}

async function fetchMesPartsCatalog(): Promise<{ id?: number; partname?: string | null }[]> {
  const raw = await getList(ENDPOINT.PARTS_LIST, {
    pagesize: 500,
    pagenumber: 0,
    serachtext: "",
  });
  return unwrapPartsList(raw);
}

/** Manual order → parts from MES parts catalog (via order lines, else full catalog). */
async function fetchManualOrderParts(orderId: number): Promise<WorkflowPartOption[]> {
  const [orderPartsRaw, catalog] = await Promise.all([
    getListById(ENDPOINT.ORDER_PARTS_LIST_BY_ORDER, orderId),
    fetchMesPartsCatalog(),
  ]);
  const orderPartRows = Array.isArray(orderPartsRaw) ? orderPartsRaw : [];
  const partNameById = new Map<string, string>();
  for (const p of catalog) {
    if (p.id != null) {
      partNameById.set(
        String(p.id),
        (p.partname || "").trim() || `Part #${p.id}`,
      );
    }
  }

  const partIdStrs = orderPartRows
    .map((r) => (r as { partid?: unknown }).partid)
    .filter((pid) => pid != null && String(pid).trim() !== "")
    .map((pid) => String(pid));
  const uniquePartIds = partIdStrs.filter((pid, idx) => partIdStrs.indexOf(pid) === idx);

  if (uniquePartIds.length > 0) {
    return uniquePartIds
      .map((pid) => Number(pid))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({
        value: String(id),
        partname: partNameById.get(String(id)) ?? `Part #${id}`,
        partid: id,
      }));
  }

  return catalog
    .filter((p) => p.id != null && Number(p.id) > 0)
    .map((p) => ({
      value: String(p.id),
      partname: (p.partname || "").trim() || `Part #${p.id}`,
      partid: Number(p.id),
    }));
}

async function loadPlatformOrderPayload(orderId: number): Promise<unknown> {
  const detail = await getList(`${ENDPOINT.PARTNER_VENDOR_ORDER}/${orderId}`, { type: "partner" });
  if (detail != null && !(Array.isArray(detail) && detail.length === 0)) {
    return detail;
  }
  return getListById(ENDPOINT.PARTNER_VENDOR_ORDER, orderId);
}

/** Platform order → parts from vendor_order_items.model_attributes.file_name */
async function fetchPlatformOrderParts(orderId: number): Promise<WorkflowPartOption[]> {
  const raw = await loadPlatformOrderPayload(orderId);
  if (raw == null) return [];

  const lineRecords = extractPartnerOrderLineRecords(raw);
  const options: WorkflowPartOption[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < lineRecords.length; i += 1) {
    const line = lineRecords[i];
    const name = extractPlatformLinePartName(line).trim();
    if (!name) continue;

    const lineId = extractLineItemId(line);
    const value = lineId != null ? `line:${lineId}` : `idx:${i}:${name}`;
    if (seen.has(value)) continue;
    seen.add(value);

    options.push({
      value,
      partname: name,
      orderPartName: name,
      orderLineId: lineId,
    });
  }

  return options;
}

export async function fetchPartsForWorkflowOrder(
  source: OrderSource,
  orderId: number,
): Promise<WorkflowPartOption[]> {
  if (source === "platform") {
    return fetchPlatformOrderParts(orderId);
  }
  return fetchManualOrderParts(orderId);
}

export function findPartOptionValue(
  parts: WorkflowPartOption[],
  wf: {
    order_source?: string | null;
    orderSource?: string | null;
    partid?: string | number | null;
    order_part_name?: string | null;
    orderPartName?: string | null;
    order_line_id?: string | number | null;
    orderLineId?: string | number | null;
  },
): string {
  const src = wf.order_source ?? wf.orderSource;
  if (src === "platform") {
    const lineId = wf.order_line_id ?? wf.orderLineId;
    if (lineId != null && String(lineId).trim() !== "") {
      const byLine = parts.find((p) => p.orderLineId != null && String(p.orderLineId) === String(lineId));
      if (byLine) return byLine.value;
    }
    const partName = (wf.order_part_name ?? wf.orderPartName ?? "").trim();
    if (partName) {
      const byName = parts.find((p) => p.orderPartName === partName || p.partname === partName);
      if (byName) return byName.value;
    }
    return "";
  }

  if (wf.partid != null && String(wf.partid).trim() !== "") {
    return String(wf.partid);
  }
  return "";
}
